// Transform the data to Laravel API resource format
function transformData(data) {
    if (data.links && data.meta) {
        return data;
    }

    return {
        data: data.data,
        links: {
            first: data.first_page_url,
            last: data.last_page_url,
            next: data.next_page_url,
            prev: data.prev_page_url,
        },
        meta: {
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            links: data.links,
            path: data.path,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
        },
    };
}

// Normalize the URL
function normalizeUrl(url) {
    return url.startsWith('/') ? `${window.location.origin}${url}` : url;
}

// Filter out unwanted params from the URL
function filterPageUrl(url, defaultParams) {
    if (!url.includes('?')) return url;

    url = new URL(normalizeUrl(url));
    const searchParams = url.searchParams;
    searchParams.delete('_ajax');

    if (searchParams.get('page') == 1) {
        searchParams.delete('page');
    }

    for (const [key, value] of Object.entries(defaultParams)) {
        if (searchParams.get(key) == value) {
            searchParams.delete(key);
        }
    }

    return url.href;
}

// Generate Bootstrap 4 pagination links, based on Laravel pagination data
function updatePaginationLinks(root, links) {
    const align = root.querySelector('.pagination').dataset.align;
    const previous = links[0];
    const next = links[links.length - 1];
    let desktopLinks = '';
    let mobileLinks = '';

    if (previous.url === null) {
        desktopLinks += `<li class="page-item disabled" aria-disabled="true" aria-label="${previous.label}"><span class="page-link" aria-hidden="true">&lsaquo;</span></li>`;
    } else {
        desktopLinks += `<li class="page-item"><a class="page-link" href="${previous.url}" rel="prev" aria-label="${previous.label}">&lsaquo;</a></li>`;
    }

    for (let i = 1; i < links.length - 1; i++) {
        const link = links[i];

        if (link.url === null) {
            desktopLinks += '<li class="page-item disabled" aria-disabled="true"><span class="page-link">...</span></li>';
            mobileLinks += '<option disabled>...</span>';
        } else {
            if (link.active === true) {
                desktopLinks += `<li class="page-item active" aria-current="page"><span class="page-link">${link.label}</span></li>`;
                mobileLinks += `<option selected>${link.label}</span>`;
            } else {
                desktopLinks += `<li class="page-item"><a class="page-link" href="${link.url}">${link.label}</a></li>`;
                mobileLinks += `<option value="${link.url}">${link.label}</span>`;
            }
        }
    }

    if (next.url === null) {
        desktopLinks += `<li class="page-item disabled" aria-disabled="true" aria-label="${next.label}"><span class="page-link" aria-hidden="true">&rsaquo;</span></li>`;
    } else {
        desktopLinks += `<li class="page-item"><a class="page-link" href="${next.url}" rel="prev" aria-label="${next.label}">&rsaquo;</a></li>`;
    }

    Alpine.mutateDom(() => {
        root.querySelectorAll('.pagination').forEach((element) => {
            if (element.querySelector('.jumper')) {
                element.querySelectorAll('.page-item .page-link:not(.jumper)').forEach((element) => element.parentElement.remove());
                element.insertAdjacentHTML(align === 'start' ? 'beforeend' : 'afterbegin', desktopLinks);
            } else {
                element.innerHTML = desktopLinks;
            }
        });

        root.querySelectorAll('.pagination-mobile select').forEach((element) => (element.innerHTML = mobileLinks));

        for (const [key, link] of Object.entries({ previous, next })) {
            root.querySelectorAll(`.pagination-mobile .${key}-page`).forEach((element) => {
                element.href = link.url || '';
                element.classList[link.url === null ? 'add' : 'remove']('disabled');
            });
        }
    });
}

// Perform a load transaction
async function performLoadTransaction(root, currentUrl, targetUrl, callback) {
    const paginationElements = root.querySelectorAll('.pagination, .pagination-mobile');

    Helper.dispatchCustomEvent('ac:listpage:loading', { url: currentUrl });

    this.loading = true;
    this.showSpinner();
    window?.Bolt?.enabled && window?.Bolt?.startProgressBar();
    paginationElements.forEach((element) => element.classList.add('loading'));

    const startTime = Date.now();
    const { data, params } = await callback();
    const endTime = Date.now();

    if (endTime - startTime < 100) {
        await new Promise((resolve) => window.setTimeout(resolve, 100 - (endTime - startTime)));
    }

    this.render(data, params);
    this.postLoad?.();

    this.$nextTick(() => {
        paginationElements.forEach((element) => element.classList.remove('loading'));
        window?.Bolt?.stopProgressBar();
        this.hideSpinner();
        this.loading = false;

        Helper.dispatchCustomEvent('ac:listpage:loaded', { url: targetUrl });
    });
}

// ListPage component
export default function (...extenders) {
    const contentElement = document.getElementById('listpage-content');

    let root;
    let _currentUrl = '';
    let _uidEffect;
    let _popstateListener;
    let _isPopFromHistory = false;
    let _scrollPositions = {};

    const component = {
        url: null,
        loading: false,
        restoreScroll: false,
        data: {},
        params: {},
        defaultParams: {},

        // Initialization on page load
        init() {
            root = this.$root;

            // Transform data
            this.data = transformData(this.data);

            // Page URL
            this.url ??= window.location.href.split('?')[0];

            // Page parameters
            this.defaultParams ??= {};

            const params = Helper.getSearchParams(this.defaultParams);

            for (const [key, value] of Object.entries(params)) {
                if (typeof value !== 'object') continue;

                params[key] = value.value;
            }

            this.params = params;

            // Target item for highlight
            const itemId = parseInt(root.dataset.itemId, 10);
            const targetItem = this.data.data.find((element) => element.id === itemId);

            if (targetItem) {
                this.$nextTick(() => {
                    const row = document.getElementById('item-' + itemId);

                    if (!row) return;

                    this.highlightRow(row);

                    const elementPosY = row.getBoundingClientRect().top;

                    if (elementPosY > window.innerHeight - 120) {
                        Helper.scrollToElement(row, Math.max(200, Math.min(500, elementPosY / 4)), 160);
                    }
                });
            }

            // Hijack paginations click
            root.querySelectorAll('.pagination').forEach((element) => {
                ['touchstart', 'mousedown'].forEach((type) => element.addEventListener(type, (event) => event.preventDefault()));

                element.addEventListener('click', (event) => {
                    event.preventDefault();

                    let target = event.target;

                    if (target.tagName !== 'A') {
                        target = target.closest('a');
                    }

                    if (target === null || target.classList.contains('jumper')) {
                        return;
                    }

                    const url = target.href;

                    if (url.length > 0 && !url.endsWith('#')) this.load(url);
                });
            });

            // Attach getPaginationInfo()
            root.querySelectorAll('.pagination-info p').forEach((element) => element.setAttribute('x-html', 'getPaginationInfo()'));

            // Save current state
            _currentUrl = window.location.href;
            window.history.scrollRestoration = 'manual';

            window.history.replaceState(
                {
                    name: 'listpage',
                    timestamp: Date.now(),
                    url: _currentUrl,
                    data: JSON.parse(JSON.stringify(this.data)),
                    params: JSON.parse(JSON.stringify(this.params)),
                },
                '',
                _currentUrl
            );

            // Handle popstate event
            _uidEffect = Alpine.effect(() => (root.dataset.uid = this.pageUidGenerator(this.url)));

            _popstateListener = (event) => {
                if (event.state?.name !== 'listpage' || root.dataset.uid !== this.pageUidGenerator(window.location.href)) return;

                _isPopFromHistory = true;
                _scrollPositions[_currentUrl] = window.scrollY;

                if (Date.now() - event.state.timestamp > 15000) {
                    this.load(event.state.url);
                } else {
                    performLoadTransaction.call(this, root, _currentUrl, event.state.url, () => ({ data: event.state.data, params: event.state.params }));
                }
            };

            if (window.Bolt) {
                Bolt.skipPopStateHandling((event) => event.state?.name === 'listpage' && root.dataset.uid === this.pageUidGenerator(window.location.href));
            }

            window.addEventListener('popstate', _popstateListener);

            // Run postInit, if available
            this.postInit?.();

            this.$nextTick(() => this.hideSpinner());
        },

        // Clean up
        destroy() {
            if (window.Bolt) {
                Bolt.skipPopStateHandling(() => false);
            }

            window.removeEventListener('popstate', _popstateListener);
            Alpine.release(_uidEffect);
        },

        // Page unique ID
        pageUidGenerator(url) {
            return new URL(url).pathname;
        },

        // Load a page
        load(targetUrl, params) {
            targetUrl ??= this.url;
            params ??= Object.assign({}, this.params, { page: 1, _ajax: 1 });

            performLoadTransaction.call(this, root, _currentUrl, targetUrl, async () => {
                for (const [key, value] of Object.entries(this.defaultParams)) {
                    if (typeof value === 'object' && typeof params[value.depends] !== 'undefined') {
                        if (params[value.depends].length === 0) {
                            params[key] = '';
                        }
                    } else {
                        if (String(params[key]) === String(value)) {
                            params[key] = '';
                        }
                    }
                }

                const target = new URL(normalizeUrl(targetUrl));

                for (const [key, value] of Object.entries(params)) {
                    if (!target.searchParams.has(key) && String(value).length > 0) {
                        target.searchParams.append(key, value);
                    }
                }

                try {
                    const data = (await axios.get(target)).data;

                    return { data };
                } catch (error) {
                    console.error(error);
                    target.searchParams.delete('_ajax');

                    if (window.Bolt) {
                        Bolt.go(target);
                    } else {
                        window.location.href = target;
                    }
                }
            });
        },

        // Render the page
        // This is retained for backwards compatibility
        refresh(data, params) {
            this.render(data, params);
        },

        // Render the page
        render(data, params) {
            data = transformData(data);

            // Preprocess links
            for (const link of data.meta.links) {
                if (typeof link.url !== 'string') continue;

                link.url = filterPageUrl(link.url, this.defaultParams);
            }

            // Set params
            if (params) {
                this.params = params;
            } else {
                this.params.page = data.meta.current_page;
            }

            // Render
            if (!document.hidden && document.startViewTransition) {
                document.documentElement.style.viewTransitionName = 'listpage';

                const transition = document.startViewTransition(() => (this.data = data));
                transition.finished.then(() => (document.documentElement.style.viewTransitionName = 'root'));
            } else {
                this.data = data;
            }

            updatePaginationLinks(root, data.meta.links);

            // Set history
            const targetUrl = data.meta.links.find((link) => link.active === true).url;

            _scrollPositions[_currentUrl] = window.scrollY;
            window.history.scrollRestoration = 'manual';

            window.history[_isPopFromHistory || targetUrl === _currentUrl ? 'replaceState' : 'pushState'](
                {
                    name: 'listpage',
                    timestamp: Date.now(),
                    url: targetUrl,
                    data: JSON.parse(JSON.stringify(this.data)),
                    params: JSON.parse(JSON.stringify(this.params)),
                },
                '',
                targetUrl
            );

            _currentUrl = targetUrl;

            // Restore scroll position
            if (this.restoreScroll && _isPopFromHistory) {
                this.$nextTick(() => {
                    window.scrollTo({ top: _scrollPositions[_currentUrl] ?? 0 });
                });
            }

            _isPopFromHistory = false;
        },

        // Show the spinner
        showSpinner(show = true) {
            contentElement?.classList?.[show ? 'add' : 'remove']('loading');
        },

        // Remove the spinner
        hideSpinner() {
            this.showSpinner(false);
        },

        // Clear the search string and reload the page, if necessary
        clearSearch(searchInput) {
            ['search', 'search_value'].forEach((param) => {
                if (!Object.hasOwn(this.params, param)) return;

                this.params[param] = '';

                if (new URL(window.location).searchParams.has(param)) {
                    this.load();
                }
            });

            searchInput.focus();
        },

        // Sort by the specified column
        sortBy(column) {
            const activeSorter = this.getActiveSorter();

            this.params.sort = column === activeSorter.column ? (activeSorter.down ? '' : '-') + activeSorter.column : '-' + column;
            this.load();
        },

        // Get sort icon for specified column
        getSortIcon(column) {
            const activeSorter = this.getActiveSorter();

            return column === activeSorter.column
                ? `<span class="fas fa-sort-${activeSorter.down ? 'down' : 'up'}"></span>`
                : '<span class="fas fa-sort" style="opacity: 0.4"></span>';
        },

        // Get current active sort column
        getActiveSorter() {
            const down = this.params.sort.startsWith('-');
            const column = this.params.sort.slice(down ? 1 : 0);

            return { column, down };
        },

        // Get pagination info
        getPaginationInfo() {
            const meta = this.data.meta;

            if (meta.total > 0) {
                if (meta.last_page === 1) {
                    const count = meta.to - meta.from + 1;

                    return trans_choice('[1] Showing :count record|[2,*] Showing :count records', count, {
                        count: count.toLocaleString(),
                    });
                } else {
                    return __('Showing :from to :to of :total records', {
                        from: meta.from.toLocaleString(),
                        to: meta.to.toLocaleString(),
                        total: meta.total.toLocaleString(),
                    });
                }
            } else {
                return __('No record');
            }
        },

        // Highlight the item's row
        highlightRow(row) {
            row.classList.add('table-success');
            row.style.transition = 'all 1.8s linear';

            window.setTimeout(() => {
                row.classList.remove('table-success');
                window.setTimeout(() => (row.style.transition = ''), 1800);
            }, 1800);
        },

        // Delete item
        deleteItem(item, callback) {
            Helper.showPageSpinner();

            return axios
                .delete(this.url + '/' + item.id, { params: Helper.getSearchParams() })
                .then((response) => {
                    this.render(response.data);

                    if (callback) {
                        callback(response.data);
                    }
                })
                .catch((error) => {
                    if (Object.hasOwn(error, 'response')) {
                        Helper.showNotificationError(error.response.data.message);
                    } else {
                        console.error(error);
                    }
                })
                .then(() => Helper.removePageSpinner());
        },
    };

    extenders.unshift(component);

    return Helper.mergeAlpineComponents.apply(Helper, extenders);
}
